import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { authApiService } from '../services/auth';
import { subscriptionService } from '../services/subscriptionService';
import { useStripeProducts } from '../hooks/useStripeProducts';
import WhatsAppSupportModal from '../components/WhatsAppSupportModal';
import RestaurantSelectorModal from '../components/RestaurantSelectorModal';
import PaymentForm from '../components/PaymentForm';
import InlineAlert from '../components/InlineAlert';
import { ApiError } from '../services/http';
import ErrorModal from '../components/ErrorModal';

interface StripePricingPlan {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  metadata: Record<string, string>;
  images: string[];
  prices: any[];
  features?: Array<{ description: string; checked?: boolean }>;
}

export default function PricingPageStripe() {
  const navigate = useNavigate();
  const { isOwner, isManager, isAuthenticated } = useAuthContext();
  const { products, loading, error, mapToPricingPlans, getPriceByCycle } = useStripeProducts();
  
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showRestaurantSelector, setShowRestaurantSelector] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<StripePricingPlan | null>(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [pageAlert, setPageAlert] = useState<{ type: 'success' | 'error' | 'info' | 'warning'; text: string } | null>(null);
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteMessage, setQuoteMessage] = useState('');

  const QUOTE_PHONE = '521234567890';

  const isQuotePlan = (plan: StripePricingPlan) => {
    const btn = (plan.metadata?.buttonText || '').toLowerCase();
    return plan.name.toLowerCase().includes('enterprise') || btn.includes('cotiz');
  };

  const openQuoteForPlan = (plan: StripePricingPlan) => {
    setSelectedPlan(plan);
    setShowQuoteModal(true);
    const preset = `Hola, me interesa el plan ${plan.name}.\n\nDetalles: `;
    setQuoteMessage(preset);
  };

  const submitQuote = () => {
    if (!selectedPlan) {
      setShowQuoteModal(false);
      return;
    }
    const base = `Hola, me interesa el plan ${selectedPlan.name}.`;
    const text = encodeURIComponent((quoteMessage && quoteMessage.trim().length > 0 ? quoteMessage : base));
    const url = `https://wa.me/${QUOTE_PHONE}?text=${text}`;
    window.open(url, '_blank');
    setShowQuoteModal(false);
  };

  const getPrice = (plan: StripePricingPlan) => {
    return billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
  };

  const getOriginalPrice = (plan: StripePricingPlan) => {
    if (billingCycle === 'yearly') {
      return plan.monthlyPrice * 12;
    }
    return undefined;
  };

  const cleanText = (text: string) => {
    // Reemplaza 'undefined ' y variantes por valores más claros
    return text
      .replace(/^undefined\s*/i, '')
      .replace(/\s+undefined$/i, '')
      .replace(/undefined/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  };

  const extractFeatures = (
    metadata: Record<string, string>,
    planFeatures?: Array<{ description: string; checked?: boolean }>
  ): Array<{ description: string; checked?: boolean }> => {
    if (Array.isArray(planFeatures) && planFeatures.length > 0) {
      return planFeatures
        .map((f) => ({ description: cleanText(String(f.description || '')), checked: f.checked }))
        .filter((f) => Boolean(f.description));
    }
    if (Array.isArray(planFeatures) && planFeatures.length > 0) {
      return planFeatures.map((f: any) => ({ description: cleanText(String(f)), checked: true }));
    }
    // Preferir JSON si existe
    const json = metadata.features_json || metadata.featuresJSON || '';
    if (json) {
      try {
        const arr = JSON.parse(json);
        if (Array.isArray(arr)) {
          return arr
            .map((f: any) =>
              typeof f === 'string'
                ? { description: cleanText(f), checked: true }
                : { description: cleanText(String(f?.description || '')), checked: Boolean(f?.checked ?? true) }
            )
            .filter((f) => Boolean(f.description));
        }
      } catch (_) {}
    }
    // Fallback: string separada por comas o saltos de línea
    const raw = metadata.features || '';
    if (raw) {
      const split = raw.includes('\n') ? raw.split('\n') : raw.split(',');
      return split
        .map((f) => ({ description: cleanText(f.trim()), checked: true }))
        .filter((f) => Boolean(f.description));
    }
    return [];
  };

  const handlePlanSelect = async (plan: StripePricingPlan) => {
    // Enfatizar reglas: solo owners o managers pueden proceder
    if (isAuthenticated && !isOwner() && !isManager()) {
      setPageAlert({ type: 'info', text: 'Solo los dueños o gerentes pueden contratar planes. Si eres dueño, pulsa "Soy restaurante" para actualizar tus permisos.' });
      return;
    }

    // Si es owner, proceder directamente (suscripción para sí mismo)
    if (isOwner()) {
      await createSubscription(plan);
      return;
    }

    // Si es manager, mostrar selector de restaurantes para especificar el owner
    if (isManager() && !isOwner()) {
      setSelectedPlan(plan);
      setShowRestaurantSelector(true);
      return;
    }

    // Para usuarios no autenticados o sin roles específicos, proceder al pago
    // (se autenticarán durante el proceso de pago)
    await createSubscription(plan);
  };

  const handleRestaurantSelect = async (id: string) => {
    setSelectedRestaurant(id);
    if (selectedPlan) {
      await createSubscription(selectedPlan);
    }
  };

  const createSubscription = async (plan: StripePricingPlan) => {
    // Si es plan de cotización, abrir modal y salir
    if (isQuotePlan(plan)) {
      openQuoteForPlan(plan);
      return;
    }
    // Verificar si el usuario está autenticado
    console.log('🔍 PricingPage: createSubscription ejecutándose...');
    console.log('🔍 PricingPage: Usuario autenticado:', isAuthenticated);
    console.log('🔍 PricingPage: Usuario actual:', { isOwner: isOwner(), isManager: isManager() });
    
    if (!isAuthenticated) {
      // Redirigir al login con returnUrl y rol owner para volver a pricing después
      console.log('🔍 PricingPage: Usuario no autenticado, redirigiendo al login...');
      navigate('/login?returnUrl=/pricing&role=owner');
      return;
    }

    if (!isOwner() && !isManager()) {
      setPageAlert({ type: 'info', text: 'Solo los dueños o gerentes pueden contratar planes. Si eres dueño, pulsa "Soy restaurante" para actualizar tus permisos.' });
      return;
    }

    // Obtener el price_id correcto según el ciclo de facturación
    const price = getPriceByCycle(products.find(p => p.id === plan.id)!, billingCycle);
    if (!price) {
      setErrorModal({ open: true, title: 'No se pudo continuar', message: 'No se encontró el precio para este plan y ciclo de facturación' });
      return;
    }

    // Verificar si ya tiene una suscripción activa con este price_id específico
    try {
      const hasActiveSubscription = await subscriptionService.checkActiveSubscriptionByPriceId(price.id);
      
      if (hasActiveSubscription) {
        setPageAlert({ type: 'warning', text: `Ya tienes una suscripción activa con el plan ${plan.name}. No puedes crear otra suscripción.` });
        return;
      }
    } catch (error) {
      console.log('No se pudo verificar suscripciones existentes:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'No se pudo verificar', message: error.message });
        return;
      }
      // Continuar con la creación si no se puede verificar
    }
    
    // Mostrar modal de pago para todos los planes
    console.log('🔍 PricingPage: Mostrando modal de pago...');
    setSelectedPlan(plan);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async () => {
    if (!selectedPlan) return;

    setIsCreatingSubscription(true);
    try {
      // La suscripción ya se creó en PaymentForm, solo redirigir
      console.log('✅ Suscripción creada exitosamente');
      
      // Redirigir al admin
      setPageAlert({ type: 'success', text: '¡Pago procesado exitosamente!' });
      setTimeout(() => navigate('/admin'), 300);
      
    } catch (error: any) {
      console.error('❌ Error después del pago:', error);
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        setErrorModal({ open: true, title: 'Pago no completado', message: error.message });
      } else {
        setErrorModal({ open: true, title: 'Error del servidor', message: 'Ocurrió un error después del pago' });
      }
    } finally {
      setIsCreatingSubscription(false);
      setShowPaymentModal(false);
    }
  };

  // const handleWhatsAppSupport = () => {
  //   setShowWhatsAppModal(true);
  // };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e9ecef] via-white to-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#22B6B2] mx-auto mb-4"></div>
          <p className="text-[#2C4358]">Cargando planes...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#e9ecef] via-white to-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <InlineAlert type="error" message={`Error cargando planes: ${error}`} />
          <button 
            onClick={() => window.location.reload()} 
            className="bg-[#22B6B2] text-white px-6 py-3 rounded-xl"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const stripePlans = mapToPricingPlans();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e9ecef] via-white to-[#f8fafc]">
      {pageAlert && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <InlineAlert type={pageAlert.type} message={pageAlert.text} onClose={() => setPageAlert(null)} />
        </div>
      )}
      {/* Aviso claro: solo dueños/gerentes */}
      {isAuthenticated && !isOwner() && !isManager() && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="font-semibold">Solo los dueños o gerentes pueden contratar planes.</div>
            <button
              onClick={async () => {
                const ok = await authApiService.upgradeCurrentUserToOwner();
                if (ok) {
                  setPageAlert({ type: 'success', text: 'Permisos actualizados. Ahora puedes contratar un plan.' });
                } else {
                  setPageAlert({ type: 'error', text: 'No se pudieron actualizar tus permisos.' });
                }
              }}
              className="px-4 py-2 bg-[#22B6B2] text-white font-bold rounded-xl hover:opacity-90"
            >
              Soy restaurante
            </button>
          </div>
        </div>
      )}
      <ErrorModal isOpen={errorModal.open} title={errorModal.title} message={errorModal.message} onClose={() => setErrorModal({ open: false, message: '' })} />
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-[#2C4358] hover:text-[#22B6B2] transition-colors"
            >
              <span className="text-2xl mr-2">←</span>
              <span className="font-semibold">Volver</span>
            </button>
            <div className="text-center">
              <div className="flex justify-center mb-2">
                <img 
                  src="/assets/ots/Servicio_a_Tiempo_icon_64x64.png" 
                  alt="Servicio a Tiempo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <h1 className="text-3xl font-bold text-[#2C4358] mb-2">Planes y Precios</h1>
              <p className="text-[#85929e]">Elige el plan perfecto para tu restaurante</p>
            </div>
            <div className="w-20"></div> {/* Spacer para centrar */}
          </div>
        </div>
      </div>

      {/* Billing Toggle */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-2xl p-2 shadow-lg border border-gray-200">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  billingCycle === 'monthly'
                    ? 'bg-[#22B6B2] text-white shadow-lg'
                    : 'text-[#85929e] hover:text-[#2C4358]'
                }`}
              >
                Mensual
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 relative ${
                  billingCycle === 'yearly'
                    ? 'bg-[#22B6B2] text-white shadow-lg'
                    : 'text-[#85929e] hover:text-[#2C4358]'
                }`}
              >
                Anual
                {billingCycle === 'yearly' && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    -10%
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Value Proposition */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-[#2C4358] mb-4">¿Por qué elegir <b>Servicio a Tiempo</b>?</h2>
          <p className="text-[#85929e] text-lg max-w-3xl mx-auto mb-8">
            Transforma tu restaurante con tecnología moderna. Ahorra tiempo, reduce errores y mejora la experiencia de tus clientes.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="text-4xl mb-4">⏱️</div>
              <h3 className="font-bold text-[#2C4358] mb-2">Ahorra 10+ horas semanales</h3>
              <p className="text-[#85929e] text-sm">Automatiza procesos manuales y enfócate en lo que importa</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="text-4xl mb-4">📈</div>
              <h3 className="font-bold text-[#2C4358] mb-2">Aumenta ventas 25%</h3>
              <p className="text-[#85929e] text-sm">Menús digitales y pedidos sin errores</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="font-bold text-[#2C4358] mb-2">ROI en 30 días</h3>
              <p className="text-[#85929e] text-sm">Recupera tu inversión rápidamente</p>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {stripePlans.map((plan: any) => {
            const features = extractFeatures(plan.metadata, plan.features);
            const isEnterprise = plan.id === 'enterprise';
            //const isProTier = !isEnterprise && /pro(fesional)?/i.test(plan.name || '') && !/starter|básico|basico|starter/i.test(plan.name || '');
            const isBasicOrStarter = !isEnterprise && /(básico|basico|starter)/i.test(plan.name || '');
            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-3xl shadow-xl border-2 transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                  plan.metadata.popular === 'true'
                    ? 'border-[#22B6B2] shadow-[#22B6B2]/20'
                    : 'border-gray-200 hover:border-[#22B6B2]/30'
                }`}
              >
                {/* Popular Badge */}
                {plan.metadata.popular === 'true' && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      ⭐ Más Popular
                    </div>
                  </div>
                )}

                {/* Intro Badge for Pro */}
                {/* Header */}
                <div className="p-8">
                  <div className="text-center mb-6">
                    <div 
                      className="w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                      style={{
                        background: `linear-gradient(135deg, ${plan.metadata.gradientStart || '#FEF3C7'}, ${plan.metadata.gradientEnd || '#FDE68A'})`
                      }}
                    >
                      {plan.images && plan.images.length > 0 ? (
                        <img 
                          src={plan.images[0]} 
                          alt={plan.name} 
                          className="w-16 h-16 object-contain"
                        />
                      ) : (
                        <span className="text-3xl">{plan.metadata.icon || '🚀'}</span>
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-[#2C4358] mb-2">{plan.name}</h3>
                    <p className="text-[#85929e] text-sm leading-relaxed">{plan.description}</p>
                    {isBasicOrStarter && (
                      <p className="text-xs text-green-600 font-semibold mt-2">Incluye 1 mes de prueba</p>
                    )}
                  </div>

                  {/* Price */}
                  <div className="text-center mb-6">
                    
                    {!isEnterprise ? (
                      <div className="flex items-center justify-center space-x-2 mb-2">
                      <span className="text-4xl font-bold text-[#2C4358]">
                        ${getPrice(plan).toLocaleString()}
                      </span>
                      {getOriginalPrice(plan) && (
                        <span className="text-lg text-[#85929e] line-through">
                          ${getOriginalPrice(plan)!.toLocaleString()}
                        </span>
                      )}
                    </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2 mb-2">
                        <span className="text-4xl font-bold text-[#2C4358]">
                        </span>
                      </div>
                    )}

                    {!isEnterprise ? (
                    <p className="text-[#85929e] text-sm">
                      {billingCycle === 'yearly' ? 'por año' : 'por mes'}
                    </p>
                    ) : (
                      <p className="text-[#85929e] text-sm">
                        Contacta a nuestro equipo de ventas
                      </p>
                    )}
                    {billingCycle === 'yearly' && (
                      <p className="text-green-600 text-sm font-semibold mt-1">
                        ¡Ahorras ${((getOriginalPrice(plan) || 0) - getPrice(plan)).toLocaleString()}!
                      </p>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-3 mb-8">
                    {features.length > 0 ? (
                      features.map((feature: { description: string; checked?: boolean }, index: number) => (
                        <div key={index} className="flex items-center space-x-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                            feature.checked ? 'bg-green-100' : 'bg-gray-200'
                          }`}>
                            <span className={`text-xs ${feature.checked ? 'text-green-600' : 'text-gray-500'}`}>✓</span>
                          </div>
                          <span className={`text-sm ${feature.checked ? 'text-[#2C4358]' : 'text-gray-500 line-through'}`}>
                            {feature.description}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="flex items-center space-x-3">
                        <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-green-600 text-xs">✓</span>
                        </div>
                        <span className="text-[#2C4358] text-sm">Plan {plan.name}</span>
                      </div>
                    )}
                  </div>

                  {/* CTA Button */}
                  {isEnterprise ? (
                    <button
                      onClick={() => handlePlanSelect(plan)}
                      disabled={isCreatingSubscription}
                      className={`w-full py-4 px-6 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 ${
                        plan.metadata.popular === 'true'
                          ? 'bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white shadow-lg hover:shadow-xl'
                          : 'bg-[#f7ebd6] text-[#2C4358] hover:bg-[#f0e0c0] border-2 border-[#f7ebd6] hover:border-[#f0e0c0]'
                      } ${isCreatingSubscription ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {isCreatingSubscription ? 'Procesando...' : plan.metadata.buttonText || 'Cotizar'}
                    </button>
                  ) : (
                  <button
                    onClick={() => (isQuotePlan(plan) ? openQuoteForPlan(plan) : handlePlanSelect(plan))}
                    disabled={isCreatingSubscription}
                    className={`w-full py-4 px-6 rounded-2xl font-bold text-lg transition-all duration-300 transform hover:scale-105 ${
                      plan.metadata.popular === 'true'
                        ? 'bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white shadow-lg hover:shadow-xl'
                        : 'bg-[#f7ebd6] text-[#2C4358] hover:bg-[#f0e0c0] border-2 border-[#f7ebd6] hover:border-[#f0e0c0]'
                    } ${isCreatingSubscription ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isCreatingSubscription ? 'Procesando...' : (isQuotePlan(plan) ? 'Cotizar' : (plan.metadata.buttonText || 'Elegir Plan'))}
                  </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional Services */}
        <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-lg border border-gray-200 p-8 mb-8">
          <h2 className="text-2xl font-bold text-[#2C4358] text-center mb-8">Servicios Adicionales</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Setup Inicial',
                price: '$1,500 MXN',
                description: 'Configuración completa de tu restaurante',
                icon: '⚙️'
              },
              {
                title: 'Capacitación',
                price: '$800 MXN',
                description: 'Sesión de capacitación personalizada',
                icon: '🎓'
              },
              // {
              //   title: 'Personalización',
              //   price: '$2,000 MXN',
              //   description: 'Diseño personalizado de tu marca',
              //   icon: '🎨'
              // },
              {
                title: 'Soporte Premium',
                price: '$500 MXN/mes',
                description: 'Soporte prioritario 24/7',
                icon: '🆘'
              }
            ].map((service, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-colors">
                <div className="text-3xl mb-3">{service.icon}</div>
                <h3 className="font-bold text-[#2C4358] mb-2">{service.title}</h3>
                <p className="text-[#22B6B2] font-bold text-lg mb-2">{service.price}</p>
                <p className="text-[#85929e] text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white/80 backdrop-blur-md rounded-3xl shadow-lg border border-gray-200 p-8">
          <h2 className="text-2xl font-bold text-[#2C4358] text-center mb-8">Preguntas Frecuentes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                question: '¿Puedo cambiar de plan en cualquier momento?',
                answer: 'Sí, puedes actualizar o degradar tu plan en cualquier momento desde tu panel de administración.'
              },
              {
                question: '¿Hay período de prueba?',
                answer: '¡Sí! Ofrecemos 1 mes de prueba gratuita en el plan starter sin compromiso para que pruebes todas las funcionalidades.'
              },
              {
                question: '¿Qué métodos de pago aceptan?',
                answer: 'Aceptamos tarjetas de crédito y débito a través de Stripe.'
              },
              {
                question: '¿El soporte está incluido?',
                answer: 'Sí, todos los planes incluyen soporte por email. Los planes superiores incluyen soporte prioritario.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-6">
                <h3 className="font-bold text-[#2C4358] mb-3">{faq.question}</h3>
                <p className="text-[#85929e] text-sm leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Final */}
        <div className="text-center py-12">
          <h2 className="text-3xl font-bold text-[#2C4358] mb-4">
            ¿Listo para digitalizar tu restaurante?
          </h2>
          <p className="text-[#85929e] text-lg mb-8 max-w-2xl mx-auto">
            Únete a cientos de restaurantes que ya están aumentando sus ventas y mejorando la experiencia de sus clientes
          </p>
        </div>
      </div>

      {/* Modales */}
      <WhatsAppSupportModal 
        isOpen={showWhatsAppModal} 
        onClose={() => setShowWhatsAppModal(false)} 
      />
      
      <RestaurantSelectorModal
        isOpen={showRestaurantSelector}
        onClose={() => setShowRestaurantSelector(false)}
        onRestaurantSelect={handleRestaurantSelect}
      />

      {/* Modal de Pago */}
      {showPaymentModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-[#2C4358]">
                Completar Pago - {selectedPlan.name}
              </h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            {/* Badge de pago seguro con Stripe */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-green-600 text-lg" aria-hidden="true">🔒</span>
              <span className="text-[13px] text-[#85929e]">
                Pago seguro con <span className="font-semibold text-[#2C4358]">Stripe</span>
              </span>
              <span className="ml-auto inline-flex items-center rounded-full border border-gray-200 px-2 py-0.5 text-[11px] text-gray-500 bg-gray-50">
                SSL
              </span>
            </div>
            
            <PaymentForm
              priceId={getPriceByCycle(products.find(p => p.id === selectedPlan.id)!, billingCycle)?.id || ''}
              restaurantId={selectedRestaurant || undefined}
              onSuccess={handlePaymentSuccess}
              onError={(errMsg) => setErrorModal({ open: true, title: 'Error en el pago', message: errMsg })}
              isProcessing={isCreatingSubscription}
              setIsProcessing={setIsCreatingSubscription}
            />
          </div>
        </div>
      )}

      {/* Modal de Cotización */}
      {showQuoteModal && selectedPlan && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-[#2C4358]">
                Solicitar Cotización - {selectedPlan.name}
              </h3>
              <button
                onClick={() => setShowQuoteModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="space-y-3 mb-4">
              <label className="text-sm text-[#85929e]">Cuéntanos qué necesitas</label>
              <textarea
                value={quoteMessage}
                onChange={(e) => setQuoteMessage(e.target.value)}
                className="w-full border border-gray-200 rounded-2xl p-4 text-sm text-[#2C4358] min-h-[140px]"
                placeholder={`Ej: Número de sucursales, cantidad de mesas, requisitos especiales...`}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowQuoteModal(false)}
                className="px-4 py-2 rounded-2xl border border-gray-200 text-[#2C4358] hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={submitQuote}
                className="px-4 py-2 rounded-2xl bg-[#22B6B2] text-white font-semibold hover:opacity-90"
              >
                Enviar por WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 